import { formatISO, subDays } from 'date-fns';

import type {
  PlaybookUsageAnalyticsRequest,
  PlaybookUsageAnalyticsResponse,
  RedlineTrendBucket as ServerRedlineTrendBucket,
  TicketUsageBucket,
} from '~/api/atlas/typedefs.autogen';
import type {
  PlaybookInsights,
  PlaybookUsageSegment,
  RedlineTrendBucket,
  TimeRange,
} from '~/features/playbooks/types/PlaybookInsights';

export type PlaybookInsightsArg = {
  playbookIds?: string[];
  timeRange?: TimeRange;
};

const DEFAULT_TIME_RANGE: TimeRange = '30d';

const TIME_RANGE_DAYS: Record<TimeRange, number> = {
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

// The dashboard offers presets rather than a date picker; the API takes an
// explicit window. Resolving here rather than in the components keeps the RTK
// cache key as `{ playbookIds, timeRange }`, so the three cards sharing this
// query still dedupe to a single request.
const mapTimeRange = (timeRange: TimeRange = DEFAULT_TIME_RANGE) => {
  const endDate = new Date();
  const startDate = subDays(endDate, TIME_RANGE_DAYS[timeRange]);

  return {
    //l10n-ignore: API contract, must be a plain calendar date
    start_date: formatISO(startDate, { representation: 'date' }),
    end_date: formatISO(endDate, { representation: 'date' }),
  };
};

export const mapPlaybookInsightsParams = (
  arg: PlaybookInsightsArg,
): PlaybookUsageAnalyticsRequest => ({
  data: {
    type: 'playbook_analytics',
    attributes: {
      playbook_ids: arg.playbookIds?.length ? arg.playbookIds : null,
      time_range: mapTimeRange(arg.timeRange),
    },
  },
});

// Every count in the schema is nullable — `null` means "unavailable", which
// reads the same as zero for a count. Rates are kept nullable so the UI can
// distinguish "no data" from a genuine 0%.
const count = (value: number | null | undefined) => value ?? 0;
const rate = (value: number | null | undefined) => value ?? null;

const mapUsageSegment = (segment: TicketUsageBucket): PlaybookUsageSegment => ({
  total: count(segment.total),
  statusCounts: {
    done: count(segment.done),
    inProgress: count(segment.in_progress),
    inactive: count(segment.inactive),
    cancelled: count(segment.cancelled),
  },
});

const mapTrendBucket = (
  bucket: ServerRedlineTrendBucket,
): RedlineTrendBucket => ({
  startDate: bucket.start_date,
  endDate: bucket.end_date,
  totalRedlinesCount: count(bucket.total_suggestions_count),
  acceptedAsIsCount: count(bucket.accepted_as_is_redlines_count),
  acceptedWithModificationCount: count(bucket.accepted_with_mod_redlines_count),
  rejectedCount: count(bucket.rejected_suggestions_count),
  acceptanceRate: rate(bucket.redline_acceptance_rate),
});

export const mapPlaybookInsightsResponse = (
  serverResponse: PlaybookUsageAnalyticsResponse,
): PlaybookInsights => {
  const {
    contracts_reviewed_rate,
    redline_acceptance_rate,
    playbook_usage,
    redline_metrics,
  } = serverResponse.data.attributes;
  const { breakdown } = redline_metrics;
  const totalRedlinesCount = count(
    redline_acceptance_rate.total_suggestions_count,
  );

  return {
    contractsReviewedRate: {
      rate: rate(contracts_reviewed_rate.rate),
      reviewedWithPlaybooksCount: count(
        contracts_reviewed_rate.reviewed_with_playbooks_count,
      ),
      participatingContractsCount: count(
        contracts_reviewed_rate.participated_tickets_count,
      ),
    },
    redlineAcceptanceRate: {
      rate: rate(redline_acceptance_rate.rate),
      acceptedRedlinesCount: count(
        redline_acceptance_rate.accepted_redlines_count,
      ),
      totalRedlinesCount,
      criticalRedlinesResolvedCount: count(
        redline_acceptance_rate.accepted_risky_redlines_count,
      ),
      criticalRedlinesCount: count(
        redline_acceptance_rate.total_risky_suggestions_count,
      ),
    },
    playbookUsage: {
      withPlaybook: mapUsageSegment(
        playbook_usage.contracts_reviewed_with_playbook_count,
      ),
      withoutPlaybook: mapUsageSegment(
        playbook_usage.contracts_reviewed_without_playbook_count,
      ),
    },
    redlineMetrics: {
      acceptanceRate: rate(breakdown.acceptance_rate),
      totalRedlinesCount,
      criticalRedlinesCount: count(
        redline_acceptance_rate.total_risky_suggestions_count,
      ),
      acceptedRedlinesCount: count(
        redline_acceptance_rate.accepted_redlines_count,
      ),
      breakdown: {
        acceptedAsIs: {
          count: count(breakdown.accepted_as_is_count),
          rate: rate(breakdown.accepted_as_is_rate),
        },
        acceptedWithModification: {
          count: count(breakdown.accepted_with_mod_count),
          rate: rate(breakdown.accepted_with_mod_rate),
        },
        rejected: {
          count: count(breakdown.rejected_suggestions_count),
          rate: rate(breakdown.rejected_suggestions_rate),
        },
      },
      trend: (redline_metrics.trends ?? []).map(mapTrendBucket),
    },
  };
};
